import { defineComponent as _defineComponent } from 'vue'
import { DebouncedFunc, debounce, isEqual } from 'lodash';
import { PaginatedParams } from 'ah-api-gateways';
import { PropType, ref, onMounted, onBeforeUnmount, watch } from 'vue';

/**
 * Base List block component
 *
 * Meant for usage alongside a <Listing> and a <fooFilter> components, controlling filter flow between both
 *
 * update-filter:  used for one-way syncing to an external value
 */


export default /*#__PURE__*/_defineComponent({
  __name: 'ListBlock',
  props: {
  /**
   * Possible set common filters of the list
   *
   * This filters will remain true despite the filters
   * set by the user
   */
  commonFilters: {
    type: Object as PropType<any>,
    required: false,
  },
  /**
   * Possible set initial filters of the list
   *
   * This filters can be overwritten by the user
   */
  defaultFilters: {
    type: Object as PropType<any>,
    required: false,
  },
  /**
   * Possible set common sort and page params of the list
   *
   * This params will remain true despite the sort and page
   * inputed by the user
   */
  commonSortAndPageParams: {
    type: Object as PropType<Partial<PaginatedParams>>,
    required: false,
  },
  /**
   * Possible set common sort and page params of the list
   *
   * This sort and page params can be overwritten by the user
   */
  defaultSortAndPageParams: {
    type: Object as PropType<Partial<PaginatedParams>>,
    required: false,
  },
},
  emits: {
  'update:filter': (_filter: any) => true,
},
  setup(__props, { emit }) {

const props = __props;





const filter = ref<any>({});

const sortAndPageParams = ref<Partial<PaginatedParams>>({});

const debouncedFilter = ref<DebouncedFunc<(filters: any) => void>>();

const debouncedSortAndPageParams = ref<DebouncedFunc<(sortAndPageParams: any) => void>>();

let blockDebounce = true;
setTimeout(() => (blockDebounce = false));

onMounted(() => {
  debouncedFilter.value = debounce((filter: any) => setFilter(filter), 800);
  debouncedSortAndPageParams.value = debounce((sortAndPageParams: any) => setSortAndPageParams(sortAndPageParams), 800);
});

function setFilter(_filter: any) {
  const newFilter = { ..._filter, ...props.commonFilters };
  if (!isEqual(newFilter, filter.value)) {
    filter.value = newFilter;
    emit('update:filter', filter.value);
  }
}

function setSortAndPageParams(_sortAndPageParams: any) {
  if (!isEqual(_sortAndPageParams, sortAndPageParams.value)) {
    sortAndPageParams.value = { ..._sortAndPageParams, ...props.commonSortAndPageParams };
  }
}

function onFilterChange(filter: any) {
  if (blockDebounce) {
    setFilter(filter);
  } else {
    debouncedFilter.value!(filter);
  }
}

function onSortAndPageParamsChange(sortAndPageParams: any) {
  if (blockDebounce) {
    setSortAndPageParams(sortAndPageParams);
  } else {
    debouncedSortAndPageParams.value!(sortAndPageParams);
  }
}

onBeforeUnmount(() => {
  debouncedFilter.value!.cancel();
  debouncedSortAndPageParams.value!.cancel();
});

function onDefaultSortChange() {
  if (props.defaultSortAndPageParams) {
    sortAndPageParams.value = props.defaultSortAndPageParams;
  }
}

watch(() => props.defaultSortAndPageParams, onDefaultSortChange, { immediate: true });

function onDefaultFiltersChange() {
  if (props.defaultFilters) {
    setFilter(props.defaultFilters);
  }
}

watch(() => props.defaultFilters, onDefaultFiltersChange, { immediate: true });

function onCommonFiltersChange() {
  setFilter({ ...filter.value, ...props.commonFilters });
}

watch(() => props.commonFilters, onCommonFiltersChange, { immediate: true, deep: true });

return { __sfc: true,props, emit, filter, sortAndPageParams, debouncedFilter, debouncedSortAndPageParams, blockDebounce, setFilter, setSortAndPageParams, onFilterChange, onSortAndPageParamsChange, onDefaultSortChange, onDefaultFiltersChange, onCommonFiltersChange }
}

})